import {
  formatIssues,
  isValidationErrorMessage,
} from '@festina-life/components'

type RequestMethod = 'GET' | 'POST'

type ServiceConfig = {
  BASE?: string
  TOKEN?:
    | string
    | ((options: { method: RequestMethod; url: string }) => Promise<string>)
}

type FetchFromServiceProps = {
  /** The `OpenAPI` configuration of the generated client owning the endpoint. */
  serviceConfig: ServiceConfig
  /** Name of the service, used in the error thrown when it is not configured. */
  serviceName: string
  /** Endpoint path appended to the service base url, leading slash included. */
  path: string
  method: RequestMethod
  body?: BodyInit
  headers?: Record<string, string>
  /** Prefix of the error thrown when the service rejects the request. */
  errorMessage: string
}

/**
 * Issues a request against a generated client's endpoint manually, applying the client's
 * authorization token and turning a rejected response into an error.
 *
 * Needed for the endpoints whose bodies the generated clients cannot send or decode,
 * such as Excel documents.
 */
export const fetchFromService = async ({
  serviceConfig,
  serviceName,
  path,
  method,
  body,
  headers,
  errorMessage,
}: FetchFromServiceProps) => {
  const { BASE, TOKEN } = serviceConfig

  if (!BASE) {
    throw new Error(`The ${serviceName} service has not been configured`)
  }

  const url = `${BASE}${path}`
  const requestHeaders = new Headers(headers)

  if (TOKEN) {
    const token =
      typeof TOKEN === 'string' ? TOKEN : await TOKEN({ method, url })

    requestHeaders.append('authorization', `Bearer ${token}`)
  }

  const response = await fetch(url, { headers: requestHeaders, body, method })

  if (!response.ok) {
    // A rejected request is not guaranteed to come with a JSON body, and its status
    // text is empty over HTTP/2, so the status code is the last resort.
    const json = await response.json().catch(() => undefined)
    const issues = isValidationErrorMessage(json) ? formatIssues(json) : ''
    const reason = issues || response.statusText || `HTTP ${response.status}`

    throw new Error(`${errorMessage}: ${reason}`)
  }

  return response
}
